import { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const DM_TEXT_LIMIT = 2000;

// Teto de upload da postagem. Precisa bater com o multer em backend/src/routes/posting.ts
// e com a validacao em postingController.uploadMedia — se um mudar sozinho, o usuario
// escolhe o arquivo e so descobre o limite depois de esperar o upload inteiro.
const MAX_UPLOAD_MB = 200;
const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;

// Faixa do catalogo de audio do Instagram. `downloadUrl` e preview e expira em
// ~1,5 dia — o que persiste na publicacao e so o audioId.
// Normalizada pelo backend (instagramAudioService.extractTracks) a partir do
// formato real da API. `previewIsExternal` = a "previa" e uma pagina do
// instagram.com, nao um arquivo: a API devolve downloadUrl null nas faixas de
// catalogo, entao nao da para tocar dentro do produto.
interface InstagramAudioTrack {
  audioId: string;
  title?: string;
  artist?: string;
  duration?: number;
  coverUrl?: string | null;
  igUsername?: string | null;
  adsEligible?: boolean;
  previewUrl?: string | null;
  // true = `previewUrl` e a PAGINA da faixa no instagram.com, nao um arquivo.
  // A Meta devolve download_url null quando nao ha previa disponivel.
  previewIsExternal?: boolean;
}

function formatTrackDuration(seconds?: number): string {
  if (!seconds) return '';
  const m = Math.floor(seconds / 60);
  const sec = Math.round(seconds % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}

interface Account {
  _id: string;
  platform: string;
  platformAccountId: string;
  username: string;
  displayName: string;
  profileId: string;
  groupName: string;
  groupId: string;
  isActive: boolean;
  connectionId?: string;
}

interface AccountGroup {
  groupId: string;
  groupName: string;
  lateProfileId: string;
  accounts: Account[];
}

interface MediaItem {
  type: 'image' | 'video';
  url: string;
  filename: string;
  size: number;
}

interface Post {
  id?: string;
  _id?: string;
  latePostId: string;
  content: string;
  status: 'draft' | 'scheduled' | 'publishing' | 'published' | 'failed' | 'partial' | 'cancelled';
  scheduledFor?: string;
  publishedAt?: string;
  platforms: Array<{
    platform: string;
    accountId: string;
    accountUsername?: string;
    status: string;
    publishedAt?: string;
    platformPostUrl?: string;
    error?: string;
  }>;
  media: MediaItem[];
  mediaItems?: Array<{ type: string; url: string; filename?: string; size?: number }>;
  createdAt: string;
  groupName?: string;
}

// Post type definitions per platform
const POST_TYPES: Record<string, Array<{
  id: string;
  label: string;
  emoji: string;
  description: string;
  requiresVideo: boolean;
  requiresMedia: boolean;
  acceptMultiple: boolean;
  formatHints: string[];
  apiContentType?: string; // value sent to Late API platformSpecificData.contentType
}>> = {
  instagram: [
    { id: 'feed', label: 'Feed', emoji: '', description: 'Foto ou video no perfil', requiresVideo: false, requiresMedia: false, acceptMultiple: false, formatHints: ['Imagem: JPG/PNG, proporcao 0.8:1 a 1.91:1, max 8MB', 'Video: MP4 H.264, max 60s para feed'] },
    { id: 'carousel', label: 'Carrossel', emoji: '', description: 'Ate 10 fotos ou videos', requiresVideo: false, requiresMedia: true, acceptMultiple: true, formatHints: ['Ate 10 itens (imagens JPG/PNG ou videos MP4)', 'Todos devem ter a mesma proporcao', 'Max 8MB por imagem', '1080x1080 recomendado'], apiContentType: 'carousel' },
    { id: 'reels', label: 'Reels', emoji: '', description: 'Video curto vertical', requiresVideo: true, requiresMedia: true, acceptMultiple: false, formatHints: ['Video: MP4 H.264/AAC, proporcao 9:16 (1080x1920)', 'Duracao: 3s a 90s', 'Tamanho: max 1GB'] },
    { id: 'story', label: 'Story', emoji: '', description: 'Desaparece em 24h', requiresVideo: false, requiresMedia: true, acceptMultiple: false, formatHints: ['Imagem: JPG/PNG, proporcao 9:16 (1080x1920)', 'Video: MP4, max 15s por story'], apiContentType: 'story' },
  ],
  facebook: [
    { id: 'feed', label: 'Feed', emoji: '', description: 'Foto, video ou texto', requiresVideo: false, requiresMedia: false, acceptMultiple: false, formatHints: ['Imagem: JPG/PNG, max 8MB', 'Video: MP4, proporcao 16:9 ou 9:16, max 240min'] },
    { id: 'carousel', label: 'Carrossel', emoji: '', description: '2-10 imagens com links', requiresVideo: false, requiresMedia: true, acceptMultiple: true, formatHints: ['2 a 10 imagens (apenas imagens, sem video)', 'Cada imagem pode ter link e titulo proprios', 'Max 8MB por imagem'], apiContentType: 'carousel' },
    { id: 'reels', label: 'Reels', emoji: '', description: 'Video curto vertical', requiresVideo: true, requiresMedia: true, acceptMultiple: false, formatHints: ['Video: MP4, proporcao 9:16 (1080x1920)', 'Duracao: 3s a 60s'], apiContentType: 'reel' },
    { id: 'story', label: 'Story', emoji: '', description: 'Desaparece em 24h', requiresVideo: false, requiresMedia: true, acceptMultiple: false, formatHints: ['Imagem ou video unico, proporcao 9:16', 'Duracao: max 20s para video'], apiContentType: 'story' },
  ],
  twitter: [
    { id: 'feed', label: 'Tweet', emoji: '', description: 'Post no Twitter/X', requiresVideo: false, requiresMedia: false, acceptMultiple: false, formatHints: ['Imagem: JPG/PNG/GIF, ate 4 imagens', 'Video: MP4, max 2min20s, 512MB', 'Texto: max 280 caracteres'] },
  ],
  tiktok: [
    { id: 'feed', label: 'Video', emoji: '', description: 'Video no TikTok', requiresVideo: true, requiresMedia: true, acceptMultiple: false, formatHints: ['Video: MP4/WebM, proporcao 9:16 (1080x1920)', 'Duracao: 3s a 10min', 'Tamanho: max 287MB (app) / 500MB (web)'] },
    { id: 'photo', label: 'Foto', emoji: '', description: 'Foto unica', requiresVideo: false, requiresMedia: true, acceptMultiple: false, formatHints: ['Imagem: JPG/PNG/WebP, max 20MB', 'Proporcao: 9:16 recomendado', 'Titulo: max 90 caracteres (truncado automaticamente)'] },
    { id: 'carousel', label: 'Carrossel de Fotos', emoji: '', description: 'Ate 35 fotos', requiresVideo: false, requiresMedia: true, acceptMultiple: true, formatHints: ['Imagens: JPG/PNG, ate 35 fotos', 'Proporcao: 9:16 recomendado', 'Titulo: max 90 caracteres (truncado automaticamente)'] },
  ],
  youtube: [
    { id: 'video', label: 'Video', emoji: '', description: 'Video longo (> 3 min)', requiresVideo: true, requiresMedia: true, acceptMultiple: false, formatHints: ['Video: MP4/MOV, resolucao min 720p, recomendado 1080p ou 4K', 'Proporcao: 16:9 (widescreen)', 'Tamanho: max 256GB ou 12h', 'Thumbnail personalizada disponivel'] },
    { id: 'shorts', label: 'Shorts', emoji: '', description: 'Video curto < 3 min (automatico)', requiresVideo: true, requiresMedia: true, acceptMultiple: false, formatHints: ['Video: MP4, proporcao 9:16 (1080x1920)', 'Duracao: max 3 minutos (detectado automaticamente pela duracao)', 'Titulo: max 100 caracteres', ' Detectado automaticamente - envie video vertical < 3min'] },
  ],
  linkedin: [
    { id: 'feed', label: 'Post', emoji: '', description: 'Post profissional', requiresVideo: false, requiresMedia: false, acceptMultiple: true, formatHints: ['Imagem: JPG/PNG, ate 20 imagens', 'Video: MP4, 3s a 10min, max 5GB', 'PDF: max 100MB (carrossel de documento)', 'Texto: max 3.000 caracteres'] },
  ],
  threads: [
    { id: 'feed', label: 'Thread', emoji: '', description: 'Post no Threads', requiresVideo: false, requiresMedia: false, acceptMultiple: false, formatHints: ['Imagem: JPG/PNG, max 8MB', 'Video: MP4 H.264/AAC, max 5 min', 'Texto: max 500 caracteres'] },
    { id: 'carousel', label: 'Carrossel', emoji: '', description: 'Ate 10 imagens', requiresVideo: false, requiresMedia: true, acceptMultiple: true, formatHints: ['Ate 10 imagens JPG/PNG/WebP/GIF', 'Max 8MB por imagem', '1080x1350 recomendado (4:5)'], apiContentType: 'carousel' },
  ],
  bluesky: [
    { id: 'feed', label: 'Post', emoji: '', description: 'Post no Bluesky', requiresVideo: false, requiresMedia: false, acceptMultiple: false, formatHints: ['Imagem: JPG/PNG, ate 4 imagens, max 1MB cada (comprimido automaticamente)', 'Video: unico por post', 'Texto: max 300 caracteres'] },
  ],
  reddit: [
    { id: 'feed', label: 'Post', emoji: '', description: 'Post no subreddit', requiresVideo: false, requiresMedia: false, acceptMultiple: false, formatHints: ['Imagem: JPG/PNG, 1 imagem', 'Video: MP4, max 15min', 'Texto: max 40.000 caracteres', 'Titulo obrigatorio: max 300 caracteres'] },
  ],
  googlebusiness: [
    { id: 'feed', label: 'Post', emoji: '', description: 'Post no Google Business', requiresVideo: false, requiresMedia: false, acceptMultiple: false, formatHints: ['Imagem: JPG/PNG, 1 imagem (sem video)', 'Texto: max 1.500 caracteres', 'Botao de acao opcional (Saiba mais, Reservar, etc.)'] },
  ],
  snapchat: [
    { id: 'story', label: 'Story', emoji: '', description: 'Desaparece em 24h', requiresVideo: false, requiresMedia: true, acceptMultiple: false, formatHints: ['Imagem ou video unico, proporcao 9:16 (1080x1920)', 'Video: max 10s por snap', 'Requer Perfil Publico'], apiContentType: 'story' },
    { id: 'spotlight', label: 'Spotlight', emoji: '', description: 'Feed de videos virais', requiresVideo: true, requiresMedia: true, acceptMultiple: false, formatHints: ['Video: proporcao 9:16, max 60s', 'Texto: max 160 caracteres', 'Requer Perfil Publico'], apiContentType: 'spotlight' },
  ],
  pinterest: [
    { id: 'feed', label: 'Pin', emoji: '', description: 'Pin no Pinterest', requiresVideo: false, requiresMedia: true, acceptMultiple: false, formatHints: ['Imagem: JPG/PNG, proporcao 2:3 recomendado (1000x1500px)', 'Video: MP4, 4s a 15min', 'Titulo: max 100 caracteres'] },
  ],
  telegram: [
    { id: 'feed', label: 'Mensagem', emoji: '✈', description: 'Texto ou midia unica', requiresVideo: false, requiresMedia: false, acceptMultiple: false, formatHints: ['Imagem: JPG/PNG, max 10MB', 'Video: MP4/MOV, max 50MB', 'Texto: max 4.096 caracteres'] },
    { id: 'carousel', label: 'Album', emoji: '', description: 'Ate 10 fotos ou videos', requiresVideo: false, requiresMedia: true, acceptMultiple: true, formatHints: ['Ate 10 itens (fotos e videos podem ser misturados)', 'Imagem: JPG/PNG/GIF/WebP, max 10MB', 'Video: MP4/MOV, max 50MB'], apiContentType: 'album' },
  ],
};

// Platform capabilities for post types (derived from POST_TYPES)
const PLATFORM_CAPABILITIES: Record<string, {
  feed: boolean;
  reels: boolean;
  story: boolean;
  carousel: boolean;
  videoOnly: boolean;
  maxImages: number;
  supportsVideo: boolean;
}> = {
  instagram: { feed: true, reels: true, story: true, carousel: true, videoOnly: true, maxImages: 10, supportsVideo: true },
  facebook: { feed: true, reels: true, story: true, carousel: true, videoOnly: true, maxImages: 10, supportsVideo: true },
  twitter: { feed: true, reels: false, story: false, carousel: false, videoOnly: true, maxImages: 4, supportsVideo: true },
  tiktok: { feed: true, reels: false, story: false, carousel: true, videoOnly: true, maxImages: 35, supportsVideo: true },
  linkedin: { feed: true, reels: false, story: false, carousel: true, videoOnly: false, maxImages: 1, supportsVideo: false },
  threads: { feed: true, reels: false, story: false, carousel: true, videoOnly: false, maxImages: 10, supportsVideo: false },
  bluesky: { feed: true, reels: false, story: false, carousel: false, videoOnly: false, maxImages: 4, supportsVideo: false },
  reddit: { feed: true, reels: false, story: false, carousel: false, videoOnly: true, maxImages: 1, supportsVideo: true },
  youtube: { feed: true, reels: false, story: false, carousel: false, videoOnly: true, maxImages: 0, supportsVideo: true },
  googlebusiness: { feed: true, reels: false, story: false, carousel: false, videoOnly: false, maxImages: 1, supportsVideo: false },
  snapchat: { feed: true, reels: false, story: true, carousel: false, videoOnly: true, maxImages: 1, supportsVideo: true },
  pinterest: { feed: true, reels: false, story: false, carousel: false, videoOnly: true, maxImages: 1, supportsVideo: true },
  telegram: { feed: true, reels: false, story: false, carousel: true, videoOnly: false, maxImages: 10, supportsVideo: true }
};

const PLATFORM_LIMITS: Record<string, number> = {
  twitter: 280,
  instagram: 2200,
  facebook: 63206,
  linkedin: 3000,
  threads: 500,
  bluesky: 300,
  tiktok: 90,
  reddit: 40000,
  youtube: 5000,
  pinterest: 500,
  snapchat: 250,
  googlebusiness: 1500,
  telegram: 4096
};

const PLATFORM_NAMES: Record<string, string> = {
  twitter: 'Twitter/X',
  instagram: 'Instagram',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  threads: 'Threads',
  bluesky: 'Bluesky',
  tiktok: 'TikTok',
  reddit: 'Reddit',
  youtube: 'YouTube',
  pinterest: 'Pinterest',
  snapchat: 'Snapchat',
  googlebusiness: 'Google Business',
  telegram: 'Telegram'
};

const PLATFORM_COLORS: Record<string, { bg: string; text: string; border: string; icon: string }> = {
  instagram: { bg: 'bg-gradient-to-r from-purple-500 to-pink-500', text: 'text-white', border: 'border-pink-300', icon: '#E4405F' },
  facebook: { bg: 'bg-blue-600', text: 'text-white', border: 'border-blue-300', icon: '#1877F2' },
  twitter: { bg: 'bg-black', text: 'text-white', border: 'border-gray-400', icon: '#000000' },
  youtube: { bg: 'bg-red-600', text: 'text-white', border: 'border-red-300', icon: '#FF0000' },
  linkedin: { bg: 'bg-blue-700', text: 'text-white', border: 'border-blue-400', icon: '#0A66C2' },
  tiktok: { bg: 'bg-black', text: 'text-white', border: 'border-gray-400', icon: '#000000' },
  reddit: { bg: 'bg-orange-600', text: 'text-white', border: 'border-orange-300', icon: '#FF4500' },
  bluesky: { bg: 'bg-sky-500', text: 'text-white', border: 'border-sky-300', icon: '#0085FF' },
  threads: { bg: 'bg-black', text: 'text-white', border: 'border-gray-400', icon: '#000000' },
  googlebusiness: { bg: 'bg-green-600', text: 'text-white', border: 'border-green-300', icon: '#4285F4' },
  telegram: { bg: 'bg-sky-500', text: 'text-white', border: 'border-sky-300', icon: '#26A5E4' },
  pinterest: { bg: 'bg-red-600', text: 'text-white', border: 'border-red-300', icon: '#E60023' },
  snapchat: { bg: 'bg-yellow-400', text: 'text-black', border: 'border-yellow-200', icon: '#FFFC00' },
};

function PlatformIcon({ platform, size = 24 }: { platform: string; size?: number }) {
  const icons: Record<string, JSX.Element> = {
    instagram: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
      </svg>
    ),
    facebook: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
    twitter: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
    youtube: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    ),
    linkedin: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    ),
    tiktok: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
      </svg>
    ),
    reddit: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
      </svg>
    ),
    bluesky: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.785 2.627 3.6 3.476 6.178 3.126-4.443.766-8.328 2.66-3.18 9.394C7.997 20.846 11.167 16.5 12 14.18c.833 2.32 3.955 6.613 8.378 8.587 5.148-6.734 1.263-8.628-3.18-9.394 2.578.35 5.393-.499 6.178-3.126C23.622 9.418 24 4.458 24 3.768c0-.69-.139-1.861-.902-2.203-.659-.3-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8z"/>
      </svg>
    ),
    threads: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.187.408-2.26 1.33-3.017.88-.724 2.104-1.138 3.542-1.199 1.048-.044 2.025.06 2.926.31-.075-1.313-.556-2.288-1.45-2.864-.965-.622-2.26-.77-3.453-.396l-.61-1.903c1.584-.508 3.396-.333 4.835.467 1.27.706 2.134 1.878 2.534 3.455.375-.107.77-.192 1.186-.25l.017-.003c1.44-.193 2.806.01 3.818.716.988.688 1.652 1.752 1.928 3.084.437 2.107-.124 4.58-2.36 6.772C18.539 23.058 15.697 23.974 12.186 24zm.06-9.963c-1.07.044-1.91.315-2.432.784-.49.44-.7 1.003-.67 1.773.027.712.335 1.25.916 1.627.64.414 1.47.58 2.344.536 1.135-.061 1.98-.473 2.58-1.22.478-.596.8-1.39.957-2.358-.82-.26-1.74-.393-2.695-.393-.335 0-.67.017-1 .05z"/>
      </svg>
    ),
    googlebusiness: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
    ),
    telegram: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
      </svg>
    ),
    pinterest: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.951-7.252 4.184 0 7.426 2.981 7.426 6.96 0 4.156-2.626 7.502-6.273 7.502-1.22 0-2.373-.633-2.763-1.385l-.752 2.868c-.27 1.027-1.006 2.31-1.503 3.093 1.164.36 2.416.554 3.725.554 6.621 0 11.987-5.367 11.987-11.987C24 5.367 18.638 0 12.017 0z"/>
      </svg>
    ),
    snapchat: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M11.996 0a8.134 8.134 0 0 0-4.636 1.488 7.37 7.37 0 0 0-2.747 4.095c-.328 1.401-.39 2.923-.195 4.348-.12.164-.265.311-.424.437-.323.255-.713.419-1.121.488-.337.057-.68.04-1.009-.047-.13-.035-.259-.076-.388-.119-.481-.161-1.026-.051-1.425.29-.399.34-.582.866-.48 1.378.096.485.421.897.868 1.1.203.091.416.166.635.22.485.122.986.161 1.483.118a3.178 3.178 0 0 0 1.258-.334c.058 1.203.491 2.361 1.238 3.28.375.462.822.86 1.323 1.18.528.336 1.085.617 1.666.837a6.223 6.223 0 0 1-1.005.811 5.923 5.923 0 0 1-1.258.552 6.002 6.002 0 0 1-1.39.243c-.476.03-.956.012-1.428-.052-.519-.071-.979-.317-1.346-.713-.367-.396-.582-.916-.628-1.446-.048-.553.078-1.107.359-1.577a.627.627 0 0 0 .071-.247c.01-.137-.024-.275-.094-.388a.653.653 0 0 0-.244-.225.666.666 0 0 0-.324-.07c-.126.002-.25.038-.36.104-.378.226-.643.606-.74 1.031-.097.426-.002.871.261 1.229.475.648 1.146 1.108 1.897 1.302.736.19 1.503.224 2.253.097 1.19-.201 2.33-.679 3.32-1.401a5.614 5.614 0 0 0 1.572-1.583 6.062 6.062 0 0 0 .564-1.111c.216.516.486 1.008.804 1.464a7.027 7.027 0 0 0 3.013 2.181c.854.341 1.764.512 2.68.503.882-.008 1.751-.186 2.563-.524a4.437 4.437 0 0 0 1.439-.966c.381-.383.651-.853.784-1.365.133-.512.115-1.045-.052-1.547a1.696 1.696 0 0 0-1.155-1.096c-.469-.136-.967-.091-1.417.127a1.674 1.674 0 0 0-.824.931c-.139.349-.158.73-.056 1.087a1.76 1.76 0 0 0 .428.777c.404.453 1.01.656 1.599.535a.862.862 0 0 1 .374.011c.143.037.266.115.352.222.086.108.132.242.133.379.001.122-.036.242-.104.343a.591.591 0 0 1-.225.19c-.382.181-.806.26-1.233.228a4.938 4.938 0 0 1-1.897-.565 5.603 5.603 0 0 1-2.071-1.745c-.266-.37-.48-.771-.637-1.192a5.414 5.414 0 0 1-.284-1.442c.866.273 1.79.351 2.7.228.694-.094 1.36-.33 1.968-.698.411-.25.753-.604.986-1.018a1.996 1.996 0 0 0 .19-1.445c-.139-.472-.444-.88-.865-1.159a2.039 2.039 0 0 0-1.229-.297c-.361.026-.713.116-1.036.265-.456.211-.968.272-1.472.176-.328-.063-.64-.176-.928-.337a2.628 2.628 0 0 1-1.02-.953c-.352-.53-.559-1.139-.6-1.774.127-.887.051-1.787-.222-2.646a7.483 7.483 0 0 0-2.316-3.411A7.632 7.632 0 0 0 11.996 0z"/>
      </svg>
    ),
  };

  return icons[platform] || (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
    </svg>
  );
}

export function PostingPage({ createOnly = false }: { createOnly?: boolean }) {
  const [accounts, setAccounts] = useState<AccountGroup[]>([]);
  const [selectedAccounts, setSelectedAccounts] = useState<Set<string>>(new Set());
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [content, setContent] = useState('');
  const [tiktokCaption, setTiktokCaption] = useState('');
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [scheduledFor, setScheduledFor] = useState('');
  const [publishNow, setPublishNow] = useState(false);
  const [firstComment, setFirstComment] = useState('');
  const [autoEnabled, setAutoEnabled] = useState(false);
  const [autoName, setAutoName] = useState('');
  const [autoKeywords, setAutoKeywords] = useState('');
  const [autoCommentReply, setAutoCommentReply] = useState('');
  const [autoDmMessage, setAutoDmMessage] = useState('');
  const [followerCheckEnabled, setFollowerCheckEnabled] = useState(false);
  const [followerCheckPromptMessage, setFollowerCheckPromptMessage] = useState('Clique no botao abaixo para liberar seu acesso.');
  const [followerCheckButtonText, setFollowerCheckButtonText] = useState('CHECAR');
  const [followerCheckFollowerMessage, setFollowerCheckFollowerMessage] = useState('Obrigado por seguir. Aqui esta o que prometemos.');
  const [followerCheckNonFollowerMessage, setFollowerCheckNonFollowerMessage] = useState('Para receber o material, siga nosso perfil e responda CHECAR novamente.');
  const [postType, setPostType] = useState<string>('feed');
  // Per-platform post type overrides: { instagram: 'reels', youtube: 'shorts', ... }
  const [platformPostTypes, setPlatformPostTypes] = useState<Record<string, string>>({});
  // Platform-specific settings
  const [youtubeSettings, setYoutubeSettings] = useState({ title: '', visibility: 'public', categoryId: '22', madeForKids: false });
  const [tiktokSettings, setTiktokSettings] = useState({ privacyLevel: 'PUBLIC_TO_EVERYONE', allowComment: true, allowDuet: true, allowStitch: true, draft: false, autoAddMusic: true });
  const [redditSettings, setRedditSettings] = useState({ title: '', subreddit: '' });
  const [instagramSettings, setInstagramSettings] = useState({ trialReel: false, graduationStrategy: 'SS_PERFORMANCE' as 'MANUAL' | 'SS_PERFORMANCE' });
  // Rotulo "feito com IA" (Instagram isAiGenerated, TikTok videoMadeWithAi, YouTube
  // containsSyntheticMedia). Aqui a midia e enviada pelo usuario, entao comeca desligado.
  const [aiGenerated, setAiGenerated] = useState(false);
  // Musica do catalogo do Instagram (Reels apenas, conta conectada por Facebook Login)
  const [instagramAudio, setInstagramAudio] = useState<{
    enabled: boolean;
    track: InstagramAudioTrack | null;
    audioVolume: number;
    videoVolume: number;
  }>({ enabled: false, track: null, audioVolume: 100, videoVolume: 100 });
  const [audioType, setAudioType] = useState<'music' | 'original_sound'>('music');
  const [audioQuery, setAudioQuery] = useState('');
  const [audioResults, setAudioResults] = useState<InstagramAudioTrack[]>([]);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);
  // Faixa tocando no player embutido. So existe quando a API devolveu ARQUIVO:
  // quando ha so o link da pagina do Instagram, a audicao e fora do produto.
  const [audioPreviewId, setAudioPreviewId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);
  const [posts, setPosts] = useState<Post[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activeTab, setActiveTab] = useState<'create' | 'list'>('create');
  const [isUploading, setIsUploading] = useState(false);
  const [filterConnectionId, setFilterConnectionId] = useState<string>('');
  const { token } = useAuth();

  // Load accounts on mount
  useEffect(() => {
    if (token) loadAccounts();
  }, [token]);

  // Load posts when on list tab
  useEffect(() => {
    if (!createOnly && activeTab === 'list' && token) {
      loadPosts();
    }
  }, [activeTab, currentPage, token, filterConnectionId, createOnly]);

  // Plataformas que nao suportam posts sociais (apenas mensageria/broadcast)
  const POSTING_UNSUPPORTED = new Set(['whatsapp', 'whatsapp_official', 'waba']);

  // Compatibilidade de midia por plataforma
  const PLATFORM_VIDEO_COMPAT: Record<string, { ok: boolean; note?: string }> = {
    instagram: { ok: true }, tiktok: { ok: true }, youtube: { ok: true },
    facebook: { ok: true }, snapchat: { ok: true }, pinterest: { ok: true },
    threads: { ok: true }, telegram: { ok: true, note: 'max 50MB' },
    twitter: { ok: true, note: 'pode exibir barras laterais' },
    linkedin: { ok: true, note: 'prefere 16:9' },
    bluesky: { ok: true, note: 'max 50MB' },
    reddit: { ok: false, note: 'nao suporta upload de video' },
    googlebusiness: { ok: false, note: 'nao aceita video' },
  };
  const PLATFORM_IMAGE_COMPAT: Record<string, { ok: boolean; note?: string }> = {
    instagram: { ok: true }, facebook: { ok: true }, twitter: { ok: true },
    linkedin: { ok: true }, threads: { ok: true }, bluesky: { ok: true },
    reddit: { ok: true }, pinterest: { ok: true }, telegram: { ok: true },
    googlebusiness: { ok: true },
    snapchat: { ok: false, note: 'requer video' },
    // Photo post do TikTok aceita de 1 a 35 imagens — foto unica e o mesmo modo
    // do carrossel, com um item so.
    tiktok: { ok: true },
    youtube: { ok: false, note: 'apenas video' },
  };
  // Plataformas que suportam carrossel (multiplas imagens deslizaveis)
  const PLATFORM_CAROUSEL_COMPAT: Record<string, { ok: boolean; note?: string }> = {
    instagram: { ok: true }, facebook: { ok: true }, tiktok: { ok: true },
    threads: { ok: true }, linkedin: { ok: true }, telegram: { ok: true },
    twitter: { ok: false, note: 'nao suporta carrossel' },
    youtube: { ok: false, note: 'nao suporta carrossel' },
    snapchat: { ok: false, note: 'nao suporta carrossel' },
    pinterest: { ok: false, note: 'nao suporta carrossel' },
    reddit: { ok: false, note: 'nao suporta carrossel' },
    bluesky: { ok: false, note: 'nao suporta carrossel' },
    googlebusiness: { ok: false, note: 'nao suporta carrossel' },
  };

  const hasVideo = mediaItems.some(m => m.type === 'video');
  const hasImage = mediaItems.some(m => m.type === 'image');
  const hasMedia = mediaItems.length > 0;
  const isCarousel = mediaItems.length > 1 && !hasVideo;

  const getPlatformMediaCompat = (platform: string): { ok: boolean; note?: string } => {
    if (!hasMedia) return { ok: false, note: 'envie uma foto, carrossel ou video primeiro' };
    if (isCarousel) return PLATFORM_CAROUSEL_COMPAT[platform] ?? { ok: true };
    if (hasVideo) return PLATFORM_VIDEO_COMPAT[platform] ?? { ok: true };
    if (hasImage) return PLATFORM_IMAGE_COMPAT[platform] ?? { ok: true };
    return { ok: true };
  };

  // Get selected platforms for capability checks
  const selectedPlatforms = useMemo(() => {
    const platforms = new Set<string>();
    for (const accountId of selectedAccounts) {
      for (const group of accounts) {
        const account = group.accounts.find(a => a._id === accountId);
        if (account) platforms.add(account.platform);
      }
    }
    return Array.from(platforms);
  }, [selectedAccounts, accounts]);
  const hasCommentAutomationPlatform = selectedPlatforms.some(p => ['instagram', 'facebook'].includes(p));
  const needsTikTokCaption = selectedPlatforms.includes('tiktok') && selectedPlatforms.length > 1;

  // Contas de Instagram selecionadas. A busca de audio precisa de UMA conta (o
  // catalogo liberado depende da conta), mas o audioId escolhido vale para todas.
  const selectedInstagramAccounts = useMemo(() => {
    const found: Account[] = [];
    for (const accountId of selectedAccounts) {
      for (const group of accounts) {
        const account = group.accounts.find(a => a._id === accountId);
        if (account?.platform === 'instagram') found.push(account);
      }
    }
    return found;
  }, [selectedAccounts, accounts]);
  const audioSearchAccountId = selectedInstagramAccounts[0]?._id || '';
  const canPickInstagramAudio = platformPostTypes['instagram'] === 'reels' && selectedInstagramAccounts.length > 0;

  // Sai de Reels (ou tira o Instagram) => a musica escolhida deixa de ser valida.
  // Sem isso o audioConfiguration ficaria pendurado e o post seria recusado na Zernio.
  useEffect(() => {
    if (canPickInstagramAudio) return;
    setInstagramAudio(prev => (prev.enabled || prev.track ? { ...prev, enabled: false, track: null } : prev));
    setAudioResults([]);
    setAudioQuery('');
    setAudioError(null);
    setAudioPreviewId(null);
  }, [canPickInstagramAudio]);

  // Busca no catalogo. Query vazia = trending (a propria Zernio devolve o que esta
  // em alta quando `q` e omitido), entao a busca roda mesmo sem o usuario digitar.
  useEffect(() => {
    if (!instagramAudio.enabled || !canPickInstagramAudio || !audioSearchAccountId) return;

    let cancelled = false;
    const timer = setTimeout(async () => {
      setAudioLoading(true);
      setAudioError(null);
      try {
        const params = new URLSearchParams({ accountId: audioSearchAccountId, audioType });
        const trimmed = audioQuery.trim();
        if (trimmed) params.set('q', trimmed);
        const response = await fetch(`/api/posting/instagram/audio?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json();
        if (cancelled) return;
        if (data.success) {
          setAudioResults(Array.isArray(data.data) ? data.data : []);
        } else {
          setAudioResults([]);
          setAudioError(data.error || 'Nao foi possivel carregar as musicas');
          console.warn('[PostingPage] catalogo de audio recusado:', data);
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error searching Instagram audio:', error);
        setAudioResults([]);
        setAudioError('Nao foi possivel carregar as musicas');
      } finally {
        if (!cancelled) setAudioLoading(false);
      }
    }, audioQuery.trim() ? 450 : 0);

    return () => { cancelled = true; clearTimeout(timer); };
  }, [instagramAudio.enabled, canPickInstagramAudio, audioSearchAccountId, audioType, audioQuery, token]);

  // Auto-deselect incompatible accounts when media changes
  useEffect(() => {
    const toRemove: string[] = [];
    for (const accountId of selectedAccounts) {
      for (const group of accounts) {
        const account = group.accounts.find(a => a._id === accountId);
        if (account) {
          const compat = getPlatformMediaCompat(account.platform);
          if (compat && !compat.ok) toRemove.push(accountId);
        }
      }
    }
    if (toRemove.length > 0) {
      setSelectedAccounts(prev => {
        const next = new Set(prev);
        toRemove.forEach(id => next.delete(id));
        return next;
      });
    }
  }, [hasMedia, hasVideo, hasImage, isCarousel, selectedAccounts, accounts]);

  // Plataformas que OBRIGAM midia (nao aceitam texto puro)
  const REQUIRES_MEDIA: Record<string, boolean> = {
    instagram: true,
    tiktok: true,
    youtube: true,
    pinterest: true,
    snapchat: true,
  };

  // Plataformas que NAO aceitam video no feed
  const NO_VIDEO_PLATFORMS: Record<string, boolean> = {
    googlebusiness: true,
    linkedin: false, // LinkedIn aceita video
  };

  // Check if media requirements are met based on per-platform post types
  const mediaRequirements = useMemo(() => {
    const hasVideo = mediaItems.some(m => m.type === 'video');
    const hasImage = mediaItems.some(m => m.type === 'image');
    const imageCount = mediaItems.filter(m => m.type === 'image').length;
    const hasMedia = mediaItems.length > 0;

    for (const platform of selectedPlatforms) {
      const pTypes = POST_TYPES[platform] || [];
      const chosenTypeId = platformPostTypes[platform] || (pTypes[0]?.id ?? 'feed');
      const chosenType = pTypes.find(t => t.id === chosenTypeId) || pTypes[0];
      const name = PLATFORM_NAMES[platform] || platform;
      if (!chosenType) continue;

      // Tipo requer video mas nao tem video
      if (chosenType.requiresVideo && !hasVideo) {
        return { valid: false, message: `${name} (${chosenType.label}) requer um video` };
      }

      // Tipo requer midia mas nao tem nenhuma
      if (chosenType.requiresMedia && !hasMedia) {
        return { valid: false, message: `${name} (${chosenType.label}) requer pelo menos uma midia` };
      }

      // Plataforma obriga midia mesmo no feed generico
      if (REQUIRES_MEDIA[platform] && !hasMedia) {
        return { valid: false, message: `${name} nao aceita posts sem midia` };
      }

      // Plataforma nao aceita video
      if (NO_VIDEO_PLATFORMS[platform] && hasVideo && !hasImage) {
        return { valid: false, message: `${name} nao aceita videos - use uma imagem` };
      }

      // Limite de imagens por plataforma
      const caps = PLATFORM_CAPABILITIES[platform];
      if (caps && caps.maxImages > 0 && imageCount > caps.maxImages && !hasVideo) {
        return { valid: false, message: `${name} permite no maximo ${caps.maxImages} imagem${caps.maxImages > 1 ? 'ns' : ''}` };
      }

      // Carrossel precisa de multiplas midias
      if (chosenTypeId === 'carousel' && mediaItems.length < 2) {
        return { valid: false, message: `${name} (Carrossel) requer pelo menos 2 itens de midia` };
      }

      // Reddit obriga titulo
      if (platform === 'reddit' && !redditSettings?.title?.trim()) {
        return { valid: false, message: `Reddit requer um titulo para o post` };
      }
    }

    return { valid: true, message: '' };
  }, [mediaItems, platformPostTypes, selectedPlatforms, redditSettings]);

  const loadAccounts = async () => {
    setIsLoadingAccounts(true);
    try {
      const response = await fetch('/api/posting/accounts', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setAccounts(data.data);
        // Auto-select first group if only one exists
        if (data.data.length === 1) {
          setSelectedGroupId(data.data[0].groupId);
        }
      } else {
        toast.error('Erro ao carregar contas disponiveis');
      }
    } catch (error) {
      console.error('Error loading accounts:', error);
      toast.error('Erro ao carregar contas disponiveis');
    } finally {
      setIsLoadingAccounts(false);
    }
  };

  const loadPosts = async () => {
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10'
      });
      if (filterConnectionId) {
        params.append('connectionId', filterConnectionId);
      }
      
      const response = await fetch(`/api/posting/posts?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setPosts(data.data.posts);
        setTotalPages(data.data.pagination.pages);
      } else {
        toast.error('Erro ao carregar posts');
      }
    } catch (error) {
      console.error('Error loading posts:', error);
      toast.error('Erro ao carregar posts');
    }
  };

  const handleAccountToggle = (accountId: string) => {
    for (const group of accounts) {
      const account = group.accounts.find(a => a._id === accountId);
      if (account) {
        const compat = getPlatformMediaCompat(account.platform);
        if (!compat.ok) {
          toast.error(compat.note || 'Plataforma incompativel com a midia enviada');
          return;
        }
      }
    }

    const newSelected = new Set(selectedAccounts);
    if (newSelected.has(accountId)) {
      newSelected.delete(accountId);
    } else {
      newSelected.add(accountId);
    }
    setSelectedAccounts(newSelected);
    
    // Reset postType if current selection doesn't support it
    if (newSelected.size > 0) {
      const platforms = new Set<string>();
      for (const id of newSelected) {
        for (const group of accounts) {
          const account = group.accounts.find(a => a._id === id);
          if (account) platforms.add(account.platform);
        }
      }
      
      // Reset per-platform types for deselected platforms
      setPlatformPostTypes(prev => {
        const updated = { ...prev };
        for (const key of Object.keys(updated)) {
          if (!Array.from(newSelected).some(id => {
            for (const g of accounts) {
              const a = g.accounts.find(acc => acc._id === id);
              if (a && a.platform === key) return true;
            }
            return false;
          })) {
            delete updated[key];
          }
        }
        return updated;
      });
    }
  };

  const handleSelectAllInGroup = (groupId: string) => {
    const group = accounts.find(g => g.groupId === groupId);
    if (!group) return;
    
    const newSelected = new Set(selectedAccounts);
    const selectableAccounts = group.accounts.filter(a => !POSTING_UNSUPPORTED.has(a.platform) && getPlatformMediaCompat(a.platform).ok);
    const allSelected = selectableAccounts.length > 0 && selectableAccounts.every(a => newSelected.has(a._id));
    
    if (allSelected) {
      selectableAccounts.forEach(a => newSelected.delete(a._id));
    } else {
      selectableAccounts.forEach(a => newSelected.add(a._id));
    }
    
    setSelectedAccounts(newSelected);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    for (const file of Array.from(files)) {
      const isVideoFile = file.type.startsWith('video/');
      const isImageFile = file.type.startsWith('image/');

      if (isVideoFile && (mediaItems.length > 0 || files.length > 1)) {
        toast.error('Video deve ser enviado sozinho. Remova as outras midias para trocar o formato.');
        continue;
      }
      if (isImageFile && mediaItems.some(item => item.type === 'video')) {
        toast.error('Nao misture fotos com video na mesma postagem.');
        continue;
      }

      // Validate size
      if (file.size > MAX_UPLOAD_BYTES) {
        toast.error(`${file.name} e muito grande. Maximo: ${MAX_UPLOAD_MB}MB`);
        continue;
      }

      // Validate type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/mov', 'video/avi'];
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: tipo nao permitido`);
        continue;
      }

      // For platforms that require video in selected type, only allow video
      const anyRequiresVideoOnly = selectedPlatforms.some(p => {
        const pTypes = POST_TYPES[p] || [];
        const chosenId = platformPostTypes[p] || (pTypes[0]?.id ?? 'feed');
        const chosen = pTypes.find(t => t.id === chosenId);
        return chosen?.requiresVideo;
      });
      if (anyRequiresVideoOnly && !file.type.startsWith('video/')) {
        toast.error('O tipo de post selecionado requer apenas arquivos de video');
        continue;
      }

      setIsUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await fetch('/api/posting/upload', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        const data = await response.json();
        
        if (data.success) {
          setMediaItems(prev => [...prev, data.data]);
          toast.success(`${file.name} enviado com sucesso`);
        } else {
          toast.error(data.error || `Erro ao enviar ${file.name}`);
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        toast.error(`Erro ao enviar ${file.name}`);
      } finally {
        setIsUploading(false);
      }
    }
    
    // Reset input
    event.target.value = '';
  };

  const removeMedia = (index: number) => {
    setMediaItems(mediaItems.filter((_, i) => i !== index));
  };

  const validateContent = () => {
    if (mediaItems.length === 0) {
      toast.error('Envie uma foto, varias fotos ou um video antes de criar o post');
      return false;
    }

    if (selectedAccounts.size === 0) {
      toast.error('Selecione pelo menos uma plataforma');
      return false;
    }

    // Check media requirements
    if (!mediaRequirements.valid) {
      toast.error(mediaRequirements.message);
      return false;
    }

    if (autoEnabled) {
      if (!hasCommentAutomationPlatform) {
        toast.error('Automacao de comentario para DM exige Instagram ou Facebook selecionado');
        return false;
      }
      if (!followerCheckEnabled && !autoDmMessage.trim()) {
        toast.error('Informe a mensagem da DM para ativar a automacao');
        return false;
      }
      if (!followerCheckEnabled && autoDmMessage.length > DM_TEXT_LIMIT) {
        toast.error(`Mensagem da DM deve ter no maximo ${DM_TEXT_LIMIT} caracteres`);
        return false;
      }
      if (followerCheckEnabled) {
        if (!selectedPlatforms.includes('instagram')) {
          toast.error('Checar seguidor exige uma conta Instagram selecionada');
          return false;
        }
        if (!followerCheckPromptMessage.trim() || !followerCheckButtonText.trim() || !followerCheckFollowerMessage.trim() || !followerCheckNonFollowerMessage.trim()) {
          toast.error('Preencha o texto do botao e as duas mensagens do fluxo de seguidor');
          return false;
        }
        if ([followerCheckPromptMessage, followerCheckFollowerMessage, followerCheckNonFollowerMessage].some(msg => msg.length > DM_TEXT_LIMIT)) {
          toast.error(`Mensagens de DM devem ter no maximo ${DM_TEXT_LIMIT} caracteres`);
          return false;
        }
      }
    }

    // Validate character limits per platform
    for (const platform of selectedPlatforms) {
      const limit = PLATFORM_LIMITS[platform];
      const platformContent = platform === 'tiktok' && needsTikTokCaption ? tiktokCaption : content;
      if (limit && platformContent.length > limit) {
        toast.error(`Conteudo excede ${limit} caracteres para ${PLATFORM_NAMES[platform]}`);
        return false;
      }
    }

    if (needsTikTokCaption && !tiktokCaption.trim()) {
      toast.error('Preencha a caption curta do TikTok');
      return false;
    }

    // Marcou "adicionar musica" e nao escolheu faixa: publicar assim sairia sem
    // musica em silencio, que e o oposto do que a pessoa pediu.
    if (instagramAudio.enabled && !instagramAudio.track?.audioId) {
      toast.error('Escolha uma musica do Instagram ou desmarque a opcao');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateContent()) return;

    setIsLoading(true);

    try {
      const platforms = Array.from(selectedAccounts).map(accountId => {
        for (const group of accounts) {
          const account = group.accounts.find(a => a._id === accountId);
          if (account) {
            const pTypes = POST_TYPES[account.platform] || [];
            const chosenTypeId = platformPostTypes[account.platform] || (pTypes[0]?.id ?? 'feed');
            const chosenType = pTypes.find(t => t.id === chosenTypeId) || pTypes[0];

            // Build platformSpecificData based on platform
            const platformSpecificData: any = {};
            if (chosenType?.apiContentType) {
              platformSpecificData.contentType = chosenType.apiContentType;
            }
            if (account.platform === 'youtube') {
              if (youtubeSettings.title) platformSpecificData.title = youtubeSettings.title;
              platformSpecificData.visibility = youtubeSettings.visibility;
              platformSpecificData.categoryId = youtubeSettings.categoryId;
              platformSpecificData.madeForKids = youtubeSettings.madeForKids;
              if (aiGenerated) platformSpecificData.containsSyntheticMedia = true;
              if (firstComment.trim()) platformSpecificData.firstComment = firstComment.trim();
            }
            if (account.platform === 'tiktok') {
              platformSpecificData.privacyLevel = tiktokSettings.privacyLevel;
              platformSpecificData.allowComment = tiktokSettings.allowComment;
              platformSpecificData.allowDuet = tiktokSettings.allowDuet;
              platformSpecificData.allowStitch = tiktokSettings.allowStitch;
              if (aiGenerated) platformSpecificData.videoMadeWithAi = true;
              if (tiktokSettings.draft) platformSpecificData.draft = true;
              // Foto unica e carrossel sao o MESMO photo post do TikTok: muda so
              // a quantidade de imagens enviadas.
              if (chosenTypeId === 'carousel' || chosenTypeId === 'photo') {
                platformSpecificData.mediaType = 'photo';
                if (tiktokSettings.autoAddMusic) platformSpecificData.auto_add_music = true;
              }
            }
            if (account.platform === 'reddit') {
              if (redditSettings.title) platformSpecificData.title = redditSettings.title;
              if (redditSettings.subreddit) platformSpecificData.subreddit = redditSettings.subreddit;
            }
            if (['instagram', 'facebook'].includes(account.platform) && firstComment.trim()) {
              platformSpecificData.firstComment = firstComment.trim();
            }
            if (account.platform === 'instagram' && aiGenerated) {
              platformSpecificData.isAiGenerated = true;
            }
            if (account.platform === 'instagram' && chosenTypeId === 'reels' && instagramSettings.trialReel) {
              platformSpecificData.trialParams = { graduationStrategy: instagramSettings.graduationStrategy };
            }
            if (account.platform === 'instagram' && chosenTypeId === 'reels' && instagramAudio.enabled && instagramAudio.track?.audioId) {
              platformSpecificData.audioConfiguration = {
                audioId: instagramAudio.track.audioId,
                audioVolume: instagramAudio.audioVolume,
                videoVolume: instagramAudio.videoVolume
              };
            }
            if (account.platform === 'linkedin' && firstComment.trim()) {
              platformSpecificData.firstComment = firstComment.trim();
            }

            return {
              platform: account.platform,
              accountId: account._id,
              ...(account.platform === 'tiktok' && needsTikTokCaption ? { customContent: tiktokCaption.trim() } : {}),
              ...(Object.keys(platformSpecificData).length > 0 ? { platformSpecificData } : {})
            };
          }
        }
        return null;
      }).filter(Boolean);

      const postData: any = {
        content: content.trim(),
        platforms,
        mediaItems,
        timezone: 'America/Sao_Paulo',
        publishNow
      };

      const automationAccountId = Array.from(selectedAccounts).find(accountId => {
        for (const group of accounts) {
          const account = group.accounts.find(a => a._id === accountId);
          if (account && ['instagram', 'facebook'].includes(account.platform)) return true;
        }
        return false;
      });

      const automationProfileId = accounts.find(group =>
        group.accounts.some(account => account._id === automationAccountId)
      )?.lateProfileId || '';

      if (autoEnabled && hasCommentAutomationPlatform && (followerCheckEnabled || autoDmMessage.trim())) {
        postData.automationSpec = {
          name: autoName.trim() || `Automacao ${new Date().toLocaleDateString('pt-BR')}`,
          keywords: autoKeywords.split(',').map(keyword => keyword.trim()).filter(Boolean),
          dmMessage: followerCheckEnabled ? followerCheckPromptMessage.trim() : autoDmMessage.trim(),
          commentReply: autoCommentReply.trim() || undefined,
          profileId: automationProfileId,
          accountId: automationAccountId || '',
          followerCheck: followerCheckEnabled ? {
            enabled: true,
            promptMessage: followerCheckPromptMessage.trim(),
            buttonText: followerCheckButtonText.trim(),
            followerMessage: followerCheckFollowerMessage.trim(),
            nonFollowerMessage: followerCheckNonFollowerMessage.trim()
          } : null
        };
      }

      if (scheduledFor && !publishNow) {
        postData.scheduledFor = scheduledFor;
      }

      const response = await fetch('/api/posting/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(postData)
      });

      const data = await response.json();

      if (data.success) {
        toast.success(publishNow ? 'Post publicado com sucesso!' : 'Post agendado com sucesso!');
        
        // Reset form
        setContent('');
        setTiktokCaption('');
        setMediaItems([]);
        setSelectedAccounts(new Set());
        setScheduledFor('');
        setPublishNow(false);
        setFirstComment('');
        setAutoEnabled(false);
        setAutoName('');
        setAutoKeywords('');
        setAutoCommentReply('');
        setAutoDmMessage('');
        setFollowerCheckEnabled(false);
        setPostType('feed');
        setPlatformPostTypes({});
        setYoutubeSettings({ title: '', visibility: 'public', categoryId: '22', madeForKids: false });
        setTiktokSettings({ privacyLevel: 'PUBLIC_TO_EVERYONE', allowComment: true, allowDuet: true, allowStitch: true, draft: false, autoAddMusic: true });
        setAiGenerated(false);
        setRedditSettings({ title: '', subreddit: '' });
        
        if (!createOnly) {
          setActiveTab('list');
          loadPosts();
        }
      } else {
        toast.error(data.error || 'Erro ao criar post');
      }
    } catch (error) {
      console.error('Error creating post:', error);
      toast.error('Erro ao criar post');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!confirm('Tem certeza que deseja deletar este post?')) return;

    try {
      const response = await fetch(`/api/posting/posts/${postId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Post deletado com sucesso');
        loadPosts();
      } else {
        toast.error(data.error || 'Erro ao deletar post');
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Erro ao deletar post');
    }
  };

  const handlePublishNow = async (postId: string) => {
    try {
      const response = await fetch(`/api/posting/posts/${postId}/publish`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Post publicado com sucesso');
        loadPosts();
      } else {
        toast.error(data.error || 'Erro ao publicar post');
      }
    } catch (error) {
      console.error('Error publishing post:', error);
      toast.error('Erro ao publicar post');
    }
  };

  const handleRetryPost = async (postId: string) => {
    try {
      const response = await fetch(`/api/posting/posts/${postId}/retry`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Post retentado com sucesso');
        loadPosts();
      } else {
        toast.error(data.error || 'Erro ao retentar post');
      }
    } catch (error) {
      console.error('Error retrying post:', error);
      toast.error('Erro ao retentar post');
    }
  };

  const handleUnpublishPost = async (postId: string, platform: string) => {
    if (!confirm(`Tem certeza que deseja despublicar este post do ${PLATFORM_NAMES[platform] || platform}? Esta acao nao pode ser desfeita.`)) return;

    try {
      const response = await fetch(`/api/posting/posts/${postId}/unpublish`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ platform })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Post despublicado com sucesso');
        loadPosts();
      } else {
        toast.error(data.error || 'Erro ao despublicar post');
      }
    } catch (error) {
      console.error('Error unpublishing post:', error);
      toast.error('Erro ao despublicar post');
    }
  };

  // Plataformas que suportam despublicacao
  const UNPUBLISH_SUPPORTED = ['threads', 'facebook', 'twitter', 'linkedin', 'youtube', 'pinterest', 'reddit', 'bluesky', 'googlebusiness', 'telegram'];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published': return 'bg-green-100 text-green-800';
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'publishing': return 'bg-yellow-100 text-yellow-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'partial': return 'bg-orange-100 text-orange-800';
      case 'cancelled': return 'bg-gray-100 text-gray-600';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'published': return 'Publicado';
      case 'scheduled': return 'Agendado';
      case 'publishing': return 'Publicando';
      case 'failed': return 'Falhou';
      case 'partial': return 'Parcial';
      case 'draft': return 'Rascunho';
      case 'cancelled': return 'Cancelado';
      default: return status;
    }
  };

  const getCharacterCount = () => {
    if (selectedPlatforms.length === 0) return { current: content.length, limit: null };
    
    const minLimit = Math.min(...selectedPlatforms.map(p => PLATFORM_LIMITS[p] || Infinity));
    return { current: content.length, limit: minLimit === Infinity ? null : minLimit };
  };

  const charCount = getCharacterCount();

  // Filter accounts by selected group
  const filteredAccounts = selectedGroupId
    ? accounts.filter(g => g.groupId === selectedGroupId)
    : accounts;

  const mediaUploadCard = (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-medium text-gray-700">1. Envie a midia</h3>
          <p className="text-xs text-gray-500">Foto unica, varias fotos para carrossel ou um video. As plataformas serao filtradas pelo formato.</p>
        </div>
        {hasMedia && (
          <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
            {isCarousel ? `Carrossel (${mediaItems.length} fotos)` : hasVideo ? 'Video' : 'Foto'}
          </span>
        )}
      </div>

      {mediaItems.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4">
          {mediaItems.map((media, index) => (
            <div key={index} className="relative group">
              <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                {media.type === 'image' ? (
                  <img src={media.url} alt={media.filename} className="w-full h-full object-cover" />
                ) : (
                  <div className="flex flex-col items-center text-gray-500">
                    <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                    </svg>
                    <span className="text-xs mt-1">Video</span>
                  </div>
                )}
              </div>
              <button
                onClick={() => removeMedia(index)}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
              >
                x
              </button>
              <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 truncate rounded-b-lg">
                {media.filename}
              </div>
            </div>
          ))}
        </div>
      )}

      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
        <div className="flex flex-col items-center">
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <p className="mt-2 text-sm text-gray-500">
            {isUploading ? 'Enviando...' : 'Clique para enviar fotos ou um video'}
          </p>
          <p className="text-xs text-gray-400">PNG, JPG, GIF, MP4 ate {MAX_UPLOAD_MB}MB</p>
        </div>
        <input
          type="file"
          className="hidden"
          accept="image/*,video/*"
          multiple={!hasVideo}
          onChange={handleFileUpload}
          disabled={isUploading}
        />
      </label>
    </div>
  );

  // Get all connections for filter dropdown
  const allConnections = useMemo(() => {
    const connections: Array<{ id: string; platform: string; username: string; groupName: string }> = [];
    for (const group of accounts) {
      for (const account of group.accounts) {
        connections.push({
          id: account._id,
          platform: account.platform,
          username: account.username,
          groupName: group.groupName
        });
      }
    }
    return connections;
  }, [accounts]);

  if (isLoadingAccounts) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando plataformas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Postar</h1>
        <p className="text-gray-600 mt-1">Crie e agende conteudo para multiplas redes sociais</p>
      </div>

      {!createOnly && (
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('create')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'create'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Criar Post
            </button>
            <button
              onClick={() => setActiveTab('list')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'list'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Meus Posts
            </button>
          </nav>
        </div>
      )}

      {activeTab === 'create' && (
        <div className="space-y-6">
          {mediaUploadCard}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Account Selection */}
          <div className="lg:col-span-1 space-y-4">
            {/* Group Selection */}
            {accounts.length > 1 && (
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Selecionar Grupo</h3>
                <div className="space-y-2">
                  {accounts.map((group) => (
                    <button
                      key={group.groupId}
                      onClick={() => {
                        setSelectedGroupId(selectedGroupId === group.groupId ? null : group.groupId);
                        setSelectedAccounts(new Set());
                      }}
                      className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors ${
                        selectedGroupId === group.groupId
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <span className="font-medium">{group.groupName}</span>
                      <span className="text-sm text-gray-500">{group.accounts.length} contas</span>
                    </button>
                  ))}
                  <button
                    onClick={() => {
                      setSelectedGroupId(null);
                      setSelectedAccounts(new Set());
                    }}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors ${
                      selectedGroupId === null
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <span className="font-medium">Todos os Grupos</span>
                    <span className="text-sm text-gray-500">{accounts.reduce((sum, g) => sum + g.accounts.length, 0)} contas</span>
                  </button>
                </div>
              </div>
            )}

            {/* Account Selection */}
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Selecionar Plataformas</h3>
              
              {accounts.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-4">Nenhuma conta conectada</p>
                  <a
                    href="/redes-sociais"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Conectar Contas
                  </a>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredAccounts.map((group) => (
                    <div key={group.groupId} className="border rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2 flex items-center justify-between">
                        <span className="font-medium text-gray-900">{group.groupName}</span>
                        <button
                          onClick={() => handleSelectAllInGroup(group.groupId)}
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          {group.accounts.every(a => selectedAccounts.has(a._id)) ? 'Desmarcar todas' : 'Selecionar todas'}
                        </button>
                      </div>
                      <div className="p-2 space-y-1">
                        {group.accounts.filter(a => !POSTING_UNSUPPORTED.has(a.platform)).map((account) => {
                          const colors = PLATFORM_COLORS[account.platform] || { bg: 'bg-gray-500', text: 'text-white', border: 'border-gray-300' };
                          const isSelected = selectedAccounts.has(account._id);
                          const compat = getPlatformMediaCompat(account.platform);
                          const incompatible = compat !== null && !compat.ok;

                          return (
                            <button
                              key={account._id}
                              onClick={() => !incompatible && handleAccountToggle(account._id)}
                              disabled={incompatible}
                              title={incompatible ? compat?.note : undefined}
                              className={`w-full flex items-center p-3 rounded-lg transition-all ${
                                incompatible
                                  ? 'bg-gray-50 border border-gray-100 opacity-40 cursor-not-allowed'
                                  : isSelected
                                  ? `${colors.bg} ${colors.text} ring-2 ring-offset-1 ${colors.border.replace('border-', 'ring-')}`
                                  : 'bg-white border border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isSelected && !incompatible ? '' : colors.bg} ${isSelected && !incompatible ? '' : colors.text}`}>
                                <PlatformIcon platform={account.platform} size={18} />
                              </div>
                              <div className="ml-3 flex-1 text-left">
                                <div className={`font-medium ${isSelected && !incompatible ? '' : 'text-gray-900'}`}>
                                  {PLATFORM_NAMES[account.platform]}
                                </div>
                                <div className={`text-xs ${isSelected && !incompatible ? 'opacity-90' : 'text-gray-500'}`}>
                                  {incompatible ? `⛔ ${compat?.note}` : compat?.note ? `Aviso ${compat.note}` : `@${account.username}`}
                                </div>
                                {!incompatible && compat?.note && (
                                  <div className="text-xs text-gray-400">@{account.username}</div>
                                )}
                              </div>
                              {isSelected && !incompatible && (
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Selected Platforms Summary */}
            {selectedAccounts.size > 0 && (
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-900 mb-2">
                  {selectedAccounts.size} plataforma{selectedAccounts.size > 1 ? 's' : ''} selecionada{selectedAccounts.size > 1 ? 's' : ''}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {selectedPlatforms.map((platform) => (
                    <span
                      key={platform}
                      className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${PLATFORM_COLORS[platform]?.bg || 'bg-gray-500'} ${PLATFORM_COLORS[platform]?.text || 'text-white'}`}
                    >
                      <PlatformIcon platform={platform} size={14} />
                      <span className="ml-1">{PLATFORM_NAMES[platform]}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Post Content */}
          <div className="lg:col-span-2 space-y-4">
            {/* Post Type Selection - Per Platform */}
            {selectedPlatforms.length > 0 && (
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Tipo de Postagem por Plataforma</h3>
                <div className="space-y-4">
                  {selectedPlatforms.map((platform) => {
                    const types = POST_TYPES[platform] || POST_TYPES['twitter'];
                    const currentType = platformPostTypes[platform] || types[0].id;
                    const selectedTypeInfo = types.find(t => t.id === currentType) || types[0];
                    return (
                      <div key={platform} className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className={`flex items-center gap-2 px-3 py-2 ${PLATFORM_COLORS[platform]?.bg || 'bg-gray-500'} ${PLATFORM_COLORS[platform]?.text || 'text-white'}`}>
                          <PlatformIcon platform={platform} size={16} />
                          <span className="font-medium text-sm">{PLATFORM_NAMES[platform]}</span>
                        </div>
                        <div className="p-3">
                          {types.length > 1 ? (
                            <div className="flex flex-wrap gap-2 mb-3">
                              {types.map((type) => (
                                <button
                                  key={type.id}
                                  onClick={() => setPlatformPostTypes(prev => ({ ...prev, [platform]: type.id }))}
                                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 text-sm font-medium transition-all ${
                                    currentType === type.id
                                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                                      : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  <span>{type.emoji}</span>
                                  <span>{type.label}</span>
                                </button>
                              ))}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-lg">{types[0].emoji}</span>
                              <span className="text-sm font-medium text-gray-700">{types[0].label}</span>
                              <span className="text-xs text-gray-500">- {types[0].description}</span>
                            </div>
                          )}
                          {/* Format hints */}
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                            <p className="text-xs font-medium text-amber-800 mb-1"> Especificacoes de formato:</p>
                            <ul className="space-y-0.5">
                              {selectedTypeInfo.formatHints.map((hint, i) => (
                                <li key={i} className="text-xs text-amber-700 flex items-start gap-1">
                                  <span className="mt-0.5 shrink-0">-</span>
                                  <span>{hint}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Platform-Specific Settings */}
            {selectedPlatforms.includes('youtube') && (
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-5 h-5 text-red-600"><PlatformIcon platform="youtube" size={20} /></div>
                  <h3 className="text-sm font-medium text-gray-700">Configuracoes do YouTube</h3>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Titulo do video <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={youtubeSettings.title}
                      onChange={(e) => setYoutubeSettings(s => ({ ...s, title: e.target.value }))}
                      placeholder="Titulo do video (max 100 caracteres)"
                      maxLength={100}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                    <p className="text-xs text-gray-400 mt-0.5">{youtubeSettings.title.length}/100 caracteres</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Visibilidade</label>
                      <select
                        value={youtubeSettings.visibility}
                        onChange={(e) => setYoutubeSettings(s => ({ ...s, visibility: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                      >
                        <option value="public"> Publico</option>
                        <option value="unlisted"> Nao listado</option>
                        <option value="private"> Privado</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Categoria</label>
                      <select
                        value={youtubeSettings.categoryId}
                        onChange={(e) => setYoutubeSettings(s => ({ ...s, categoryId: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                      >
                        <option value="22"> Pessoas e Blogs</option>
                        <option value="24"> Entretenimento</option>
                        <option value="23"> Comedia</option>
                        <option value="25"> Noticias</option>
                        <option value="26"> Como fazer</option>
                        <option value="27"> Educacao</option>
                        <option value="28"> Ciencia e Tecnologia</option>
                        <option value="17">⚽ Esportes</option>
                        <option value="10"> Musica</option>
                        <option value="20"> Games</option>
                        <option value="1"> Filmes</option>
                        <option value="15"> Animais</option>
                        <option value="2"> Automoveis</option>
                      </select>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={youtubeSettings.madeForKids}
                      onChange={(e) => setYoutubeSettings(s => ({ ...s, madeForKids: e.target.checked }))}
                      className="rounded"
                    />
                    <span className="text-xs text-gray-600">Conteudo feito para criancas (COPPA)</span>
                  </label>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                    <p className="text-xs text-blue-700"> <strong>Shorts automatico:</strong> Videos verticais (9:16) com menos de 3 minutos sao detectados automaticamente como Shorts pelo YouTube.</p>
                  </div>
                </div>
              </div>
            )}

            {selectedPlatforms.some(p => ['instagram', 'tiktok', 'youtube'].includes(p)) && (
              <div className="bg-white rounded-lg shadow p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={aiGenerated}
                    onChange={(e) => setAiGenerated(e.target.checked)}
                    className="mt-0.5 rounded"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-700">Marcar como feito com IA</span>
                    <span className="block text-xs text-gray-500 mt-0.5">A rede exibe o aviso de conteudo gerado por IA. Vale para Instagram, TikTok e YouTube.</span>
                  </span>
                </label>
              </div>
            )}

            {selectedPlatforms.includes('tiktok') && (
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-5 h-5"><PlatformIcon platform="tiktok" size={20} /></div>
                  <h3 className="text-sm font-medium text-gray-700">Configuracoes do TikTok</h3>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Privacidade</label>
                    <select
                      value={tiktokSettings.privacyLevel}
                      onChange={(e) => setTiktokSettings(s => ({ ...s, privacyLevel: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                      <option value="PUBLIC_TO_EVERYONE"> Publico</option>
                      <option value="MUTUAL_FOLLOW_FRIENDS"> Amigos mutuos</option>
                      <option value="FOLLOWER_OF_CREATOR"> Seguidores</option>
                      <option value="SELF_ONLY"> Apenas eu</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={tiktokSettings.allowComment} onChange={(e) => setTiktokSettings(s => ({ ...s, allowComment: e.target.checked }))} className="rounded" />
                      <span className="text-xs text-gray-600">Comentarios</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={tiktokSettings.allowDuet} onChange={(e) => setTiktokSettings(s => ({ ...s, allowDuet: e.target.checked }))} className="rounded" />
                      <span className="text-xs text-gray-600">Dueto</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={tiktokSettings.allowStitch} onChange={(e) => setTiktokSettings(s => ({ ...s, allowStitch: e.target.checked }))} className="rounded" />
                      <span className="text-xs text-gray-600">Stitch</span>
                    </label>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={tiktokSettings.draft} onChange={(e) => setTiktokSettings(s => ({ ...s, draft: e.target.checked }))} className="rounded" />
                    <span className="text-xs text-gray-600">Salvar como rascunho no TikTok Creator Inbox</span>
                  </label>
                  {['carousel', 'photo'].includes(platformPostTypes['tiktok']) && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={tiktokSettings.autoAddMusic} onChange={(e) => setTiktokSettings(s => ({ ...s, autoAddMusic: e.target.checked }))} className="rounded" />
                      <span className="text-xs text-gray-600"> Deixar o TikTok selecionar musica automaticamente para o post de foto</span>
                    </label>
                  )}
                </div>
              </div>
            )}

            {selectedPlatforms.includes('instagram') && platformPostTypes['instagram'] === 'reels' && (
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-5 h-5 text-pink-600"><PlatformIcon platform="instagram" size={20} /></div>
                  <h3 className="text-sm font-medium text-gray-700">Configuracoes do Instagram Reels</h3>
                </div>
                <div className="space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                    <input
                      type="checkbox"
                      checked={instagramSettings.trialReel}
                      onChange={(e) => setInstagramSettings(s => ({ ...s, trialReel: e.target.checked }))}
                      className="rounded mt-0.5"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-800"> Reel de Teste</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        O Reel e exibido <strong>apenas para nao-seguidores</strong> inicialmente. O Instagram testa o desempenho antes de exibir para seus seguidores. Util para validar conteudo sem arriscar o alcance da sua base atual.
                      </div>
                    </div>
                  </label>
                  {instagramSettings.trialReel && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Estrategia de graduacao</label>
                      <select
                        value={instagramSettings.graduationStrategy}
                        onChange={(e) => setInstagramSettings(s => ({ ...s, graduationStrategy: e.target.value as 'MANUAL' | 'SS_PERFORMANCE' }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                      >
                        <option value="SS_PERFORMANCE">Automatico - o Instagram publica para seguidores se o desempenho for bom</option>
                        <option value="MANUAL">Manual - voce decide quando publicar para seguidores pelo app</option>
                      </select>
                    </div>
                  )}

                  <div className="pt-3 border-t border-gray-200">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={instagramAudio.enabled}
                        onChange={(e) => {
                          const enabled = e.target.checked;
                          setInstagramAudio(s => ({ ...s, enabled, track: enabled ? s.track : null }));
                          if (!enabled) { setAudioResults([]); setAudioError(null); setAudioPreviewId(null); }
                        }}
                        className="rounded mt-0.5"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-800">Adicionar musica do Instagram</div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          Usa o catalogo licenciado do proprio Instagram. So funciona em <strong>Reels</strong> e em contas conectadas pelo <strong>Facebook Login</strong>.
                        </div>
                      </div>
                    </label>

                    {instagramAudio.enabled && (
                      <div className="mt-3 space-y-3">
                        {selectedInstagramAccounts.length > 1 && (
                          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                            Voce selecionou {selectedInstagramAccounts.length} contas do Instagram. A busca usa o catalogo de <strong>@{selectedInstagramAccounts[0]?.username}</strong> e a faixa escolhida vai para todas — se alguma nao tiver acesso a ela, o post daquela conta falha.
                          </div>
                        )}

                        <div className="flex gap-2">
                          <select
                            value={audioType}
                            onChange={(e) => { setAudioType(e.target.value as 'music' | 'original_sound'); setAudioPreviewId(null); }}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                          >
                            <option value="music">Musicas</option>
                            <option value="original_sound">Sons originais</option>
                          </select>
                          <input
                            type="text"
                            value={audioQuery}
                            onChange={(e) => setAudioQuery(e.target.value)}
                            placeholder="Buscar por nome ou artista — deixe vazio para ver o que esta em alta"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                          />
                        </div>

                        {!audioQuery.trim() && !audioError && (
                          <div className="text-xs text-gray-500">
                            Em alta agora no catalogo liberado para publicacao por API — pode diferir do que aparece dentro do app.
                          </div>
                        )}

                        {audioError && (
                          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">{audioError}</div>
                        )}

                        {audioLoading && <div className="text-xs text-gray-500">Carregando...</div>}

                        {!audioLoading && !audioError && audioResults.length === 0 && (
                          <div className="text-xs text-gray-500">Nenhuma faixa encontrada.</div>
                        )}

                        {audioResults.length > 0 && (
                          <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                            {audioResults.map(track => {
                              const isSelected = instagramAudio.track?.audioId === track.audioId;
                              return (
                                <div
                                  key={track.audioId}
                                  className={`flex items-center gap-2 p-2 cursor-pointer ${isSelected ? 'bg-pink-50' : 'hover:bg-gray-50'}`}
                                  onClick={() => setInstagramAudio(s => ({ ...s, track }))}
                                >
                                  <input type="radio" checked={isSelected} onChange={() => setInstagramAudio(s => ({ ...s, track }))} className="shrink-0" />
                                  {track.coverUrl && (
                                    <img src={track.coverUrl} alt="" className="h-9 w-9 shrink-0 rounded object-cover" />
                                  )}
                                  <div className="min-w-0 flex-1">
                                    <div className="text-sm text-gray-800 truncate">{track.title || 'Sem titulo'}</div>
                                    <div className="text-xs text-gray-500 truncate">
                                      {track.artist || 'Desconhecido'}
                                      {track.duration ? ` - ${formatTrackDuration(track.duration)}` : ''}
                                      {track.igUsername ? ` - @${track.igUsername}` : ''}
                                    </div>
                                  </div>
                                  {track.previewUrl && (
                                    track.previewIsExternal === false ? (
                                      // Arquivo de audio de verdade: toca aqui mesmo.
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setAudioPreviewId(prev => (prev === track.audioId ? null : track.audioId)); }}
                                        className="shrink-0 px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100"
                                      >
                                        {audioPreviewId === track.audioId ? 'Parar' : 'Ouvir'}
                                      </button>
                                    ) : (
                                      // Sem arquivo (download_url null na Meta): so resta a
                                      // pagina da faixa no Instagram, em outra aba.
                                      <a
                                        href={track.previewUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="shrink-0 px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100"
                                        title="Ouvir no Instagram (abre em nova aba)"
                                      >
                                        Ouvir ↗
                                      </a>
                                    )
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {audioPreviewId && (
                          <audio
                            key={audioPreviewId}
                            src={audioResults.find(t => t.audioId === audioPreviewId)?.previewUrl || undefined}
                            autoPlay
                            controls
                            onEnded={() => setAudioPreviewId(null)}
                            className="w-full"
                          />
                        )}

                        {instagramAudio.track && (
                          <div className="space-y-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
                            <div className="text-xs text-gray-700">
                              Selecionada: <strong>{instagramAudio.track.title || instagramAudio.track.audioId}</strong>
                              {instagramAudio.track.artist ? ` - ${instagramAudio.track.artist}` : ''}
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Volume da musica: {instagramAudio.audioVolume}</label>
                              <input
                                type="range" min={0} max={100}
                                value={instagramAudio.audioVolume}
                                onChange={(e) => setInstagramAudio(s => ({ ...s, audioVolume: Number(e.target.value) }))}
                                className="w-full"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Volume do audio do video: {instagramAudio.videoVolume}</label>
                              <input
                                type="range" min={0} max={100}
                                value={instagramAudio.videoVolume}
                                onChange={(e) => setInstagramAudio(s => ({ ...s, videoVolume: Number(e.target.value) }))}
                                className="w-full"
                              />
                              <div className="text-xs text-gray-500 mt-0.5">Deixe em 0 para silenciar o som proprio do video.</div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {selectedPlatforms.includes('reddit') && (
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-5 h-5 text-orange-600"><PlatformIcon platform="reddit" size={20} /></div>
                  <h3 className="text-sm font-medium text-gray-700">Configuracoes do Reddit</h3>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Titulo do post <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={redditSettings.title}
                      onChange={(e) => setRedditSettings(s => ({ ...s, title: e.target.value }))}
                      placeholder="Titulo do post (max 300 caracteres)"
                      maxLength={300}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Subreddit (sem r/)</label>
                    <input
                      type="text"
                      value={redditSettings.subreddit}
                      onChange={(e) => setRedditSettings(s => ({ ...s, subreddit: e.target.value }))}
                      placeholder="ex: socialmedia"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Content */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-700">Conteudo</h3>
                <div className={`text-sm ${charCount.limit && charCount.current > charCount.limit ? 'text-red-600' : 'text-gray-500'}`}>
                  {charCount.limit ? `${charCount.current}/${charCount.limit}` : `${charCount.current} caracteres`}
                </div>
              </div>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Digite o conteudo do seu post..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={5}
                maxLength={5000}
              />
            </div>

            {needsTikTokCaption && (
              <div className="bg-white rounded-lg shadow p-4">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-700">Caption TikTok</h3>
                  <span className={`text-sm ${tiktokCaption.length > 90 ? 'text-red-600' : 'text-gray-500'}`}>{tiktokCaption.length}/90</span>
                </div>
                <textarea
                  value={tiktokCaption}
                  onChange={(e) => setTiktokCaption(e.target.value.slice(0, 90))}
                  placeholder="Versao curta somente para TikTok..."
                  rows={2}
                  maxLength={90}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>
            )}

            {/* Scheduling */}
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Agendamento</h3>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setPublishNow(true)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    publishNow
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-xl mb-1"></div>
                  <div className="font-medium">Publicar Agora</div>
                </button>
                <button
                  onClick={() => setPublishNow(false)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    !publishNow
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-xl mb-1"></div>
                  <div className="font-medium">Agendar</div>
                </button>
              </div>
              
              {!publishNow && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Data e Hora
                  </label>
                  <input
                    type="datetime-local"
                    value={scheduledFor}
                    onChange={(e) => setScheduledFor(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min={new Date(Date.now() + 15 * 60 * 1000).toISOString().slice(0, 16)}
                  />
                </div>
              )}
            </div>

            {/* First Comment (for LinkedIn/Instagram/YouTube/Facebook) */}
            {selectedPlatforms.some(p => ['linkedin', 'instagram', 'youtube', 'facebook'].includes(p)) && (
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-1">Primeiro Comentario</h3>
                <p className="text-xs text-gray-500 mb-3">Disponivel para: Instagram, Facebook, LinkedIn, YouTube</p>
                <textarea
                  value={firstComment}
                  onChange={(e) => setFirstComment(e.target.value)}
                  placeholder="Adicione um primeiro comentario (util para links no LinkedIn)..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  rows={2}
                />
              </div>
            )}

            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-1">Automacao comentario para DM</h3>
                  <p className="text-xs text-gray-500">Cria resposta publica e mensagem privada quando alguem comentar palavras-chave.</p>
                </div>
                <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={autoEnabled}
                    onChange={(e) => setAutoEnabled(e.target.checked)}
                    disabled={!hasCommentAutomationPlatform}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                  />
                  Ativar
                </label>
              </div>

              {!hasCommentAutomationPlatform && (
                <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
                  Disponivel somente para contas Instagram ou Facebook selecionadas.
                </p>
              )}

              {autoEnabled && hasCommentAutomationPlatform && (
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Nome da automacao</label>
                    <input
                      type="text"
                      value={autoName}
                      onChange={(e) => setAutoName(e.target.value)}
                      placeholder="Ex: Enviar cupom por DM"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Palavras-chave</label>
                    <input
                      type="text"
                      value={autoKeywords}
                      onChange={(e) => setAutoKeywords(e.target.value)}
                      placeholder="QUERO, INFO, CUPOM"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Resposta no comentario</label>
                    <input
                      type="text"
                      value={autoCommentReply}
                      onChange={(e) => setAutoCommentReply(e.target.value)}
                      placeholder="Te mandei no privado."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  {!followerCheckEnabled && <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Mensagem da DM <span className="text-red-500">*</span></label>
                    <textarea
                      value={autoDmMessage}
                      onChange={(e) => setAutoDmMessage(e.target.value)}
                      maxLength={DM_TEXT_LIMIT}
                      placeholder="Mensagem enviada automaticamente no direct..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                  </div>}
                  <div className="md:col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <label className="mb-3 flex items-start gap-2 text-sm font-medium text-slate-700">
                      <input
                        type="checkbox"
                        checked={followerCheckEnabled}
                        onChange={(e) => setFollowerCheckEnabled(e.target.checked)}
                        disabled={!selectedPlatforms.includes('instagram')}
                        className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                      />
                      <span>
                        Checar se e seguidor
                        <span className="block text-xs font-normal text-slate-500">Use uma resposta do lead no direct para ler o instagramProfile.isFollower e seguir por mensagens diferentes.</span>
                      </span>
                    </label>
                    {!selectedPlatforms.includes('instagram') && (
                      <p className="mb-3 rounded bg-amber-50 px-3 py-2 text-xs text-amber-700">Disponivel somente para Instagram.</p>
                    )}
                    {followerCheckEnabled && selectedPlatforms.includes('instagram') && (
                      <div className="grid gap-3 md:grid-cols-2">
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Mensagem que acompanha o botao</label>
                          <textarea
                            value={followerCheckPromptMessage}
                            onChange={(e) => setFollowerCheckPromptMessage(e.target.value)}
                            maxLength={DM_TEXT_LIMIT}
                            rows={2}
                            placeholder="Clique no botao abaixo para liberar seu acesso."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Texto do botao</label>
                          <input
                            type="text"
                            value={followerCheckButtonText}
                            onChange={(e) => setFollowerCheckButtonText(e.target.value)}
                            placeholder="CHECAR"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Mensagem para quem segue</label>
                          <textarea
                            value={followerCheckFollowerMessage}
                            onChange={(e) => setFollowerCheckFollowerMessage(e.target.value)}
                            maxLength={DM_TEXT_LIMIT}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Mensagem para quem nao segue</label>
                          <textarea
                            value={followerCheckNonFollowerMessage}
                            onChange={(e) => setFollowerCheckNonFollowerMessage(e.target.value)}
                            maxLength={DM_TEXT_LIMIT}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                          />
                          <p className="mt-1 text-xs text-gray-500">O mesmo botao inicial sera enviado junto para a pessoa tentar novamente depois de seguir.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => {
                  setContent('');
                  setTiktokCaption('');
                  setMediaItems([]);
                  setSelectedAccounts(new Set());
                  setScheduledFor('');
                  setPublishNow(false);
                  setFirstComment('');
                  setAutoEnabled(false);
                  setAutoName('');
                  setAutoKeywords('');
                  setAutoCommentReply('');
                  setAutoDmMessage('');
                  setFollowerCheckEnabled(false);
                  setPostType('feed');
                  setPlatformPostTypes({});
                  setYoutubeSettings({ title: '', visibility: 'public', categoryId: '22', madeForKids: false });
                  setTiktokSettings({ privacyLevel: 'PUBLIC_TO_EVERYONE', allowComment: true, allowDuet: true, allowStitch: true, draft: false, autoAddMusic: true });
                  setAiGenerated(false);
                  setRedditSettings({ title: '', subreddit: '' });
                }}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Limpar
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isLoading || selectedAccounts.size === 0 || (!content.trim() && mediaItems.length === 0)}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? 'Processando...' : (publishNow ? 'Publicar Agora' : 'Agendar Post')}
              </button>
            </div>
          </div>
          </div>
        </div>
      )}

      {!createOnly && activeTab === 'list' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Meus Posts</h2>
                <p className="text-sm text-gray-600 mt-1">Gerencie seus posts agendados e publicados</p>
              </div>
              
              {/* Connection Filter */}
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Filtrar por:</label>
                <select
                  value={filterConnectionId}
                  onChange={(e) => {
                    setFilterConnectionId(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Todas as conexoes</option>
                  {allConnections.map((conn) => (
                    <option key={conn.id} value={conn.id}>
                      {PLATFORM_NAMES[conn.platform]} - @{conn.username} ({conn.groupName})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            {posts.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum post encontrado</h3>
                <p className="text-gray-500 mb-6">Comece criando seu primeiro post para aparecer aqui</p>
                <button
                  onClick={() => setActiveTab('create')}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Criar Primeiro Post
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {posts.map((post) => (
                  <div key={post.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        {/* Header with status and date */}
                        <div className="flex items-center flex-wrap gap-3 mb-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(post.status)}`}>
                            {getStatusText(post.status)}
                          </span>
                          <div className="flex items-center text-sm text-gray-500">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {new Date(post.createdAt).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                          {post.scheduledFor && (
                            <div className="flex items-center text-sm text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              {new Date(post.scheduledFor).toLocaleString('pt-BR')}
                            </div>
                          )}
                        </div>
                        
                        {/* Content */}
                        <div className="mb-4">
                          <p className="text-gray-900 text-base leading-relaxed">{post.content}</p>
                        </div>
                        
                        {/* Media Preview */}
                        {((post.media?.length ?? 0) > 0 || (post.mediaItems?.length ?? 0) > 0) && (
                          <div className="mb-4">
                            <div className="flex gap-2">
                              {(post.media?.length ? post.media : (post.mediaItems || [])).slice(0, 4).map((media, index) => (
                                <div key={index} className="relative group">
                                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                                    {media.type === 'image' ? (
                                      <img src={media.url} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                      <div className="flex flex-col items-center text-gray-400">
                                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                                          <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                                        </svg>
                                        <span className="text-xs mt-1">Video</span>
                                      </div>
                                    )}
                                  </div>
                                  {index === 3 && post.media.length > 4 && (
                                    <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center text-white text-sm font-medium">
                                      +{post.media.length - 4}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Platforms */}
                        <div className="flex flex-wrap gap-2">
                          {post.platforms.map((platform, index) => {
                            const colors = PLATFORM_COLORS[platform.platform] || { bg: 'bg-gray-500', text: 'text-white' };
                            return (
                              <div
                                key={index}
                                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium ${colors.bg} ${colors.text}`}
                              >
                                <PlatformIcon platform={platform.platform} size={16} />
                                <div className="flex flex-col">
                                  <span>{PLATFORM_NAMES[platform.platform]}</span>
                                  <span className="text-xs opacity-90">@{allConnections.find(c => c.id === platform.accountId)?.username || platform.accountId}</span>
                                </div>
                                <span className={`ml-1 px-2 py-0.5 rounded text-xs ${
                                  platform.status === 'published' ? 'bg-green-500 bg-opacity-20' :
                                  platform.status === 'failed' ? 'bg-red-500 bg-opacity-20' :
                                  platform.status === 'publishing' ? 'bg-yellow-500 bg-opacity-20' :
                                  'bg-white bg-opacity-20'
                                }`}>
                                  {getStatusText(platform.status)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col gap-2 ml-6">
                        {post.status === 'scheduled' && (
                          <button
                            onClick={() => handlePublishNow(post.latePostId)}
                            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            Publicar Agora
                          </button>
                        )}
                        {(post.status === 'failed' || post.status === 'partial') && (
                          <button
                            onClick={() => handleRetryPost(post.latePostId)}
                            className="inline-flex items-center px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-sm font-medium"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Retentar
                          </button>
                        )}
                        {(post.status === 'draft' || post.status === 'scheduled') && (
                          <button
                            onClick={() => handleDeletePost(post.latePostId)}
                            className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Deletar
                          </button>
                        )}
                        {post.status === 'published' && (
                          <div className="flex flex-col gap-1">
                            {post.platforms
                              .filter(p => p.status === 'published' && UNPUBLISH_SUPPORTED.includes(p.platform))
                              .map((p, i) => (
                                <button
                                  key={i}
                                  onClick={() => handleUnpublishPost(post.latePostId, p.platform)}
                                  className="inline-flex items-center px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs font-medium"
                                >
                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                  </svg>
                                  Remover do {PLATFORM_NAMES[p.platform]}
                                </button>
                              ))
                            }
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-4 mt-8 pt-6 border-t border-gray-200">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      Anterior
                    </button>
                    <div className="flex items-center px-4 py-2 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium text-gray-900">
                        Pagina {currentPage} de {totalPages}
                      </span>
                    </div>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Proxima
                      <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
